'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  BarChart2,
  CalendarDays,
  Map,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Wind,
  Settings,
  HelpCircle,
  Trash2,
  FileText,
} from 'lucide-react';
import { useAerodrome } from '@/context/AerodromeContext';

interface NavItem {
  id: string;
  label: string;
  labelFr: string;
  icon: React.ReactNode;
  href: string;
  badge?: number;
}

const navItems: NavItem[] = [
  {
    id: 'nav-dashboard',
    label: 'Dashboard',
    labelFr: 'Tableau de bord',
    icon: <LayoutDashboard size={20} />,
    href: '/weather-overview-dashboard',
  },
  {
    id: 'nav-emagram',
    label: 'Emagram',
    labelFr: 'Émagramme',
    icon: <BarChart2 size={20} />,
    href: '/interactive-emagram',
  },
  {
    id: 'nav-forecast',
    label: 'Multi-Day',
    labelFr: 'Prévisions 7J',
    icon: <CalendarDays size={20} />,
    href: '/multi-day-forecast',
  },
  {
    id: 'nav-map',
    label: 'Map',
    labelFr: 'Carte Météo',
    icon: <Map size={20} />,
    href: '/meteorological-map',
  },
  {
    id: 'nav-briefing',
    label: 'Briefing',
    labelFr: 'Briefing Quotidien',
    icon: <FileText size={20} />,
    href: '/daily-briefing',
  },
  {
    id: 'nav-guide',
    label: 'Guide',
    labelFr: 'Guide Débutant',
    icon: <BookOpen size={20} />,
    href: '/beginner-s-guide',
  },
];

interface SidebarProps {
  currentPath: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { selectedAerodrome, setSelectedAerodrome, recentAerodromes, removeRecentAerodrome } = useAerodrome();

  const isActive = (href: string) => currentPath === href || currentPath.startsWith(href);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile hamburger */}
      <button
        className="fixed top-3 left-3 z-50 lg:hidden p-3 rounded-xl touch-manipulation"
        style={{ backgroundColor: 'var(--sidebar-bg)', border: '1px solid var(--sidebar-border)' }}
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle navigation"
      >
        <Wind size={22} style={{ color: 'var(--accent-cyan)' }} />
      </button>

      {/* Sidebar */}
      <aside
        className={`
          flex flex-col h-full z-50 transition-all duration-300 ease-in-out flex-shrink-0
          ${collapsed ? 'w-16' : 'w-60'}
          ${mobileOpen ? 'fixed left-0 top-0 w-60' : 'hidden lg:flex'}
        `}
        style={{
          backgroundColor: 'var(--sidebar-bg)',
          borderRight: '1px solid var(--sidebar-border)',
        }}
      >
        {/* Logo */}
        <div
          className={`flex items-center h-16 px-3 flex-shrink-0 ${collapsed ? 'justify-center' : 'gap-3'}`}
          style={{ borderBottom: '1px solid var(--sidebar-border)' }}
        >
          <AppLogo size={32} />
          {!collapsed && (
            <span
              className="font-bold text-lg tracking-tight"
              style={{ color: 'var(--info-blue)', fontFamily: 'DM Sans, sans-serif' }}
            >
              SoarCast
            </span>
          )}
        </div>

        {/* Active terrain indicator */}
        {!collapsed && (
          <div className="mx-3 mt-3 px-3 py-2 rounded-lg" style={{ backgroundColor: '#0d1a2e', border: '1px solid var(--sidebar-border)' }}>
            <p className="text-xs font-medium" style={{ color: '#9fb2c8' }}>TERRAIN ACTIF</p>
            <p className="font-mono text-sm font-semibold mt-0.5 truncate" style={{ color: 'var(--accent-cyan)' }}>
              {selectedAerodrome.icao} — {selectedAerodrome.name}
            </p>
          </div>
        )}

        {/* Nav items */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.id}
                href={item.href}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group relative
                  ${active ? '' : 'hover:bg-white/5'}
                `}
                style={active
                  ? { backgroundColor: 'rgba(126,200,227,0.14)', color: '#ffffff' }
                  : { color: '#9fb2c8' }
                }
                onClick={() => setMobileOpen(false)}
                title={collapsed ? item.labelFr : undefined}
              >
                <span
                  className={`flex-shrink-0 ${active ? '' : 'group-hover:text-[#d4e8f0]'}`}
                  style={active ? { color: 'var(--accent-cyan)' } : {}}
                >
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className={`text-sm font-medium ${active ? '' : 'group-hover:text-[#d4e8f0]'}`}>
                    {item.labelFr}
                  </span>
                )}
                {active && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r"
                    style={{ background: 'linear-gradient(180deg, var(--accent-cyan) 0%, var(--info-blue) 100%)' }}
                  />
                )}
                {item.badge && !collapsed && (
                  <span
                    className="ml-auto text-xs font-mono px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: 'rgba(248,113,113,0.2)', color: 'var(--danger-red)' }}
                  >
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Recent terrains list */}
          {!collapsed && recentAerodromes.length > 0 && (
            <div className="mt-4">
              <p className="px-3 text-xs font-medium mb-1" style={{ color: '#9fb2c8' }}>
                TERRAINS RÉCENTS ({recentAerodromes.length}/{10})
              </p>
              <div className="space-y-0.5">
                {recentAerodromes.map((aerodrome) => {
                  const isSelected = aerodrome.icao === selectedAerodrome.icao;
                  return (
                    <div
                      key={aerodrome.icao}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg group"
                      style={{ backgroundColor: isSelected ? 'rgba(126,200,227,0.14)' : 'transparent' }}
                    >
                      <button
                        className="flex-1 flex items-center gap-2 text-left min-w-0"
                        onClick={() => { setSelectedAerodrome(aerodrome); setMobileOpen(false); }}
                        title={`${aerodrome.icao} — ${aerodrome.name} (${aerodrome.lat.toFixed(3)}, ${aerodrome.lon.toFixed(3)})`}
                      >
                        <span className="font-mono text-xs font-semibold flex-shrink-0" style={{ color: isSelected ? 'var(--accent-cyan)' : '#ffffff' }}>
                          {aerodrome.icao}
                        </span>
                        <span className="text-xs truncate" style={{ color: isSelected ? '#d7ecf8' : '#9fb2c8' }}>
                          {aerodrome.name}
                        </span>
                      </button>
                      <button
                        className="flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-150 hover:bg-red-500/20"
                        style={{ color: 'var(--danger-red)' }}
                        onClick={(e) => { e.stopPropagation(); removeRecentAerodrome(aerodrome.icao); }}
                        title={`Supprimer ${aerodrome.icao}`}
                        aria-label={`Supprimer ${aerodrome.icao} de la liste`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        {/* Bottom section */}
        <div className="px-2 pb-4 space-y-1" style={{ borderTop: '1px solid var(--sidebar-border)', paddingTop: '12px' }}>
          <Link
            key="nav-settings"
            href="/settings"
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group relative
              ${isActive('/settings') ? '' : 'hover:bg-white/5'}
            `}
            style={isActive('/settings') ? { backgroundColor: 'rgba(126,200,227,0.14)', color: '#ffffff' } : { color: '#b7c7d8' }}
            onClick={() => setMobileOpen(false)}
          >
            <Settings size={20} style={isActive('/settings') ? { color: 'var(--accent-cyan)' } : {}} className={isActive('/settings') ? '' : 'group-hover:text-[#ffffff]'} />
            {!collapsed && <span className={`text-sm font-medium ${isActive('/settings') ? '' : 'group-hover:text-[#d4e8f0]'}`}>Paramètres</span>}
            {isActive('/settings') && (
              <span
                className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r"
                style={{ backgroundColor: 'var(--accent-cyan)' }}
              />
            )}
          </Link>
          <Link
            key="nav-help"
            href="/beginner-s-guide"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 hover:bg-white/5 group"
            style={{ color: '#b7c7d8' }}
          >
            <HelpCircle size={20} className="group-hover:text-[#ffffff]" />
            {!collapsed && <span className="text-sm font-medium group-hover:text-[#ffffff]">Aide</span>}
          </Link>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center h-8 w-8 rounded-full mx-auto mb-2 transition-all duration-150 hover:bg-white/10"
          style={{ border: '1px solid var(--sidebar-border)', color: '#b7c7d8' }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        {/* Version */}
        <div className="flex justify-center pb-3">
          {collapsed ? (
            <span className="text-sm font-mono font-bold" style={{ color: 'var(--accent-cyan)' }}>v1</span>
          ) : (
            <span className="text-sm font-mono font-bold" style={{ color: 'var(--accent-cyan)' }}>v1.0.21 bis</span>
          )}
        </div>
      </aside>
    </>
  );
}
